/**
 * Agregar un turno al calendario del cliente.
 *
 * Sirve para cualquier calendario, sin integrarse con ninguno: un archivo .ics
 * (iCalendar, RFC 5545) lo abren Google Calendar, Apple Calendar, Outlook y
 * Thunderbird por igual. El OAuth de Google que ya existe en la app es otra
 * cosa: sincroniza la agenda *del negocio*, no la del cliente que reserva.
 *
 * Hay una copia de `construirICS` en backend/calendarInvite.js, que arma el
 * mismo archivo para adjuntarlo al mail de confirmación. Son dos runtimes
 * distintos (navegador y Node) y no comparten paquete; si tocás el formato acá,
 * tocalo allá.
 */

/**
 * Zona horaria de los turnos.
 *
 * Argentina no usa horario de verano, así que un offset fijo de -3 alcanza y
 * evita depender de una tabla de zonas. Cuando la app soporte negocios en otra
 * zona (ver el ítem de zona horaria en docs/notas/backlog.md), esto tiene que
 * pasar a salir del negocio.
 */
const OFFSET_HORAS = -3;

/**
 * Date -> "YYYY-MM-DD" en hora local.
 *
 * A mano y no con `toISOString`, que pasa a UTC: para un turno de la mañana en
 * Argentina eso todavía cae el día anterior y el turno se agendaría un día antes.
 */
export function aFechaISO(fecha) {
  const d = fecha instanceof Date ? fecha : new Date(fecha);
  if (Number.isNaN(d.getTime())) return '';

  const mes = String(d.getMonth() + 1).padStart(2, '0');
  const dia = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mes}-${dia}`;
}

/** Escapa los caracteres que en iCalendar tienen significado propio. */
function escapar(texto) {
  return String(texto ?? '')
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/\r?\n/g, '\\n');
}

/**
 * Parte las líneas largas como pide el RFC, continuando con un espacio. Sin
 * esto, una descripción larga hace que algunos clientes (Outlook entre ellos)
 * descarten el archivo entero.
 *
 * El RFC habla de 75 octetos y acá cortamos por carácter: con acentos la línea
 * queda unos bytes más larga, que ningún cliente rechaza. Cortar por octeto sí
 * podría partir un UTF-8 al medio, que es el error que rompe el archivo.
 */
function plegar(linea) {
  if (linea.length <= 75) return linea;

  const partes = [linea.slice(0, 75)];
  let resto = linea.slice(75);
  while (resto.length > 74) {
    partes.push(' ' + resto.slice(0, 74));
    resto = resto.slice(74);
  }
  if (resto) partes.push(' ' + resto);
  return partes.join('\r\n');
}

/** Date -> 20260801T180000Z */
function aFormatoUTC(fecha) {
  return fecha.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
}

/**
 * Convierte la fecha y hora del turno (que están en hora del negocio) al
 * instante UTC que corresponde.
 */
function aUTC(fechaISO, hora) {
  const [anio, mes, dia] = String(fechaISO).split('-').map(Number);
  const [h, m] = String(hora).split(':').map(Number);
  return new Date(Date.UTC(anio, mes - 1, dia, h - OFFSET_HORAS, m));
}

/** El turno como inicio y fin en UTC. */
function rango({ fechaISO, hora, duracionMinutos }) {
  const inicio = aUTC(fechaISO, hora);
  const fin = new Date(inicio.getTime() + (Number(duracionMinutos) || 60) * 60000);
  return { inicio, fin };
}

/**
 * Arma el contenido de un archivo .ics con el turno.
 *
 * Incluye un aviso 2 horas antes: el negocio ya manda un recordatorio por mail,
 * pero la alarma la dispara el teléfono del cliente aunque no abra el correo, y
 * las inasistencias son el problema que este producto viene a resolver.
 */
export function construirICS({
  titulo,
  descripcion = '',
  lugar = '',
  fechaISO,
  hora,
  duracionMinutos,
  uid,
}) {
  const { inicio, fin } = rango({ fechaISO, hora, duracionMinutos });

  const lineas = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Aturno//Reservas//ES',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:${escapar(uid)}`,
    `DTSTAMP:${aFormatoUTC(new Date())}`,
    `DTSTART:${aFormatoUTC(inicio)}`,
    `DTEND:${aFormatoUTC(fin)}`,
    `SUMMARY:${escapar(titulo)}`,
    descripcion ? `DESCRIPTION:${escapar(descripcion)}` : null,
    lugar ? `LOCATION:${escapar(lugar)}` : null,
    'STATUS:CONFIRMED',
    'BEGIN:VALARM',
    'TRIGGER:-PT2H',
    'ACTION:DISPLAY',
    `DESCRIPTION:${escapar(titulo)}`,
    'END:VALARM',
    'END:VEVENT',
    'END:VCALENDAR',
  ].filter(Boolean);

  // CRLF y no \n: el RFC lo exige y hay clientes que se plantan si falta.
  return lineas.map(plegar).join('\r\n') + '\r\n';
}

/**
 * Link para agregarlo a Google Calendar sin descargar nada ni pedir permisos:
 * es la pantalla de "evento nuevo" precargada.
 */
export function linkGoogleCalendar({
  titulo,
  descripcion = '',
  lugar = '',
  fechaISO,
  hora,
  duracionMinutos,
}) {
  const { inicio, fin } = rango({ fechaISO, hora, duracionMinutos });

  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: titulo,
    dates: `${aFormatoUTC(inicio)}/${aFormatoUTC(fin)}`,
  });
  if (descripcion) params.set('details', descripcion);
  if (lugar) params.set('location', lugar);

  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

/**
 * Lo mismo para Outlook en la web (outlook.com y Microsoft 365): abre el
 * formulario de evento nuevo ya cargado.
 *
 * Las fechas van en ISO con la Z, y no en el formato compacto del .ics, porque
 * es el único que el deeplink de Outlook interpreta; con `20260801T180000Z`
 * abre el formulario vacío, sin decir por qué.
 */
export function linkOutlookCalendar({
  titulo,
  descripcion = '',
  lugar = '',
  fechaISO,
  hora,
  duracionMinutos,
}) {
  const { inicio, fin } = rango({ fechaISO, hora, duracionMinutos });

  const params = new URLSearchParams({
    path: '/calendar/action/compose',
    rru: 'addevent',
    subject: titulo,
    startdt: inicio.toISOString(),
    enddt: fin.toISOString(),
  });
  if (descripcion) params.set('body', descripcion);
  if (lugar) params.set('location', lugar);

  return `https://outlook.live.com/calendar/0/deeplink/compose?${params.toString()}`;
}

/** Dispara la descarga del .ics en el navegador. */
export function descargarICS(datos, nombreArchivo = 'turno.ics') {
  const contenido = construirICS(datos);
  const blob = new Blob([contenido], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = nombreArchivo;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  // Sin esto el blob queda retenido hasta que se cierre la pestaña.
  URL.revokeObjectURL(url);
}
